import dayjs from 'dayjs';
import { Dispatch, MutableRefObject, SetStateAction, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { VideoPlayer } from '@/components/Video';
import { TProgramBroadcast } from '@/features/broadcast-schedule/types';
import { useBroadcastScheduleContext } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/context/BroadcastScheduleProvider';
import { ESegmentToolkit } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useGoNextOnEnded } from '@/features/broadcast-schedule-program/hooks/useGoNextOnEnded';
import { TSegment } from '@/features/segments/types';

import { useBroadcastScheduleProgramBodySegmentDetailCardBodySegmentNavigationState } from '../hooks/useBroadcastScheduleProgramBodySegmentDetailCardBodySegmentNavigationState';

import { BroadcastScheduleProgramBodySegmentDetailCardBodySegmentList } from './BroadcastScheduleProgramBodySegmentDetailCardBodySegmentList';
import { BroadcastScheduleProgramBodySegmentDetailCardBodySegmentTranscription } from './BroadcastScheduleProgramBodySegmentDetailCardBodySegmentTranscription';

type TBroadcastScheduleProgramBodySegmentDetailCardBodyProps = {
  items: TSegment[];
  programBroadcast: TProgramBroadcast;
  setSegmentItems: Dispatch<SetStateAction<TSegment[]>>;
};
export function BroadcastScheduleProgramBodySegmentDetailCardBody({
  items,
  programBroadcast,
  setSegmentItems,
}: TBroadcastScheduleProgramBodySegmentDetailCardBodyProps) {
  const {
    visibleBox,
    activeSegment,
    drawerLogo,
    videoPlayerRef,
    pendingSeekTime,
    videoControlRef,
    cutForm,
    presenceData,
    pinnedId,
    setPendingSeekTime,
    onDrawnRect,
    setVisibleBox,
    setPlayerVideoCurrentTime,
    setPlayerVideoDuration,
    setPlayerVideoIsPlaying,
  } = useBroadcastScheduleProgram();

  const { setAutoplayingClips, handleEndPresence, handleGoNextPresence, handleSeekPresence } =
    useBroadcastScheduleContext();

  const { handleNextItem, hasNextItem } =
    useBroadcastScheduleProgramBodySegmentDetailCardBodySegmentNavigationState({ data: items });

  const [videoHasError, setVideoHasError] = useState(false);

  const startDatetime = useWatch({ control: cutForm.control, name: 'start_datetime' });
  const endDatetime = useWatch({ control: cutForm.control, name: 'end_datetime' });

  const markers = (() => {
    if (!activeSegment) return [];

    const segmentStart = dayjs(activeSegment.start_datetime);
    const segmentDuration = activeSegment.duration_in_seconds || 1;

    const result: { pos: number; type: 'start' | 'end' }[] = [];

    if (startDatetime) {
      const startOffset = dayjs(startDatetime).diff(segmentStart, 'second', true);
      if (startOffset >= 0 && startOffset <= segmentDuration) {
        result.push({ pos: startOffset / segmentDuration, type: 'start' });
      }
    }

    if (endDatetime) {
      const endOffset = dayjs(endDatetime).diff(segmentStart, 'second', true);
      if (endOffset >= 0 && endOffset <= segmentDuration) {
        result.push({ pos: endOffset / segmentDuration, type: 'end' });
      }
    }

    return result;
  })();

  const handleGoNext = useGoNextOnEnded({
    hasNextItem,
    handleNextItem,
    videoControlRef,
    skip: pinnedId === ESegmentToolkit.TIMEKEEPING,
  });

  const handlePause = () => {
    setAutoplayingClips(false);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-center">
        <VideoPlayer
          activeBox={visibleBox}
          checkProgress={handleGoNextPresence}
          controls={false}
          customControls
          drawerLogo={drawerLogo}
          presenceData={presenceData}
          markers={markers}
          onChangeVideoHasError={setVideoHasError}
          onDrawnRect={onDrawnRect}
          onEnded={handleGoNext}
          onEndPresence={handleEndPresence}
          onPause={handlePause}
          pendingSeekTime={pendingSeekTime}
          ref={videoPlayerRef}
          setActiveBox={setVisibleBox}
          setPendingSeekTime={setPendingSeekTime}
          setPlayerVideoCurrentTime={setPlayerVideoCurrentTime}
          setPlayerVideoDuration={setPlayerVideoDuration}
          setPlayerVideoIsPlaying={setPlayerVideoIsPlaying}
          url={activeSegment?.presigned_url || ''}
          checkSeek={handleSeekPresence}
          onRefReady={ref => {
            if (videoControlRef && 'current' in videoControlRef) {
              (videoControlRef as MutableRefObject<unknown>).current = ref;
            }
          }}
        />
      </div>
      <BroadcastScheduleProgramBodySegmentDetailCardBodySegmentList
        programBroadcast={programBroadcast}
        items={items}
        setSegmentItems={setSegmentItems}
        videoHasError={videoHasError}
      />
      <BroadcastScheduleProgramBodySegmentDetailCardBodySegmentTranscription />
    </div>
  );
}
